/* BSP schreiben: Entity-Lump (und optional der Textur-Lump) austauschen, alle anderen Lumps bleiben unverändert. */
import type { Doc } from '../model/types.ts';
import type { BspFile } from './bsp-parse.ts';
import { LUMP, NLUMPS } from './bsp-parse.ts';

export function entsToText(doc: Doc): string {
  const out: string[] = [];
  doc.ents.forEach((e) => {
    out.push('{');
    Object.keys(e.kv).forEach((k) => { if (e.kv[k] !== undefined) out.push('"' + k + '" "' + String(e.kv[k]) + '"'); });
    out.push('}');
  });
  return out.join('\n') + '\n';
}

/** Ersatz für einen Textur-Eintrag: wird zu einer externen Textur (nur Kopf, keine Pixel) */
export type TexReplacement = Record<number, { name: string; w: number; h: number }>;

/** Textur-Lump neu aufbauen: repl = { miptexIndex: {name, w, h} }. Ersetzte Einträge werden externe Texturen (nur Kopf, keine Pixel). */
function texLump(b: BspFile, repl: TexReplacement): Uint8Array {
  const src = new Uint8Array(b.buf), L = b.lumps[LUMP.TEX], dv = new DataView(b.buf), xo = L.ofs;
  if (L.len < 4) return src.subarray(L.ofs, L.ofs + L.len);
  const cnt = dv.getInt32(xo, true), offs: number[] = [];
  for (let i = 0; i < cnt; i++) offs.push(dv.getInt32(xo + 4 + i * 4, true));
  const sorted = offs.filter((o) => o > 0).sort((a, c) => a - c);
  const entries: (Uint8Array | null)[] = offs.map((o) => {
    if (o <= 0) return null;
    const nx = sorted.find((x) => x > o);
    return src.subarray(xo + o, xo + (nx === undefined ? L.len : nx));
  });
  entries.forEach((_e, i) => {
    const r = repl[i]; if (!r) return;
    const h = new Uint8Array(40), v = new DataView(h.buffer);
    for (let k = 0; k < 15 && k < r.name.length; k++) h[k] = r.name.charCodeAt(k) & 255;
    v.setUint32(16, r.w, true); v.setUint32(20, r.h, true);
    entries[i] = h;
  });
  let pos = 4 + cnt * 4; const o2: number[] = [];
  entries.forEach((e) => { if (!e) { o2.push(-1); return; } pos = (pos + 3) & ~3; o2.push(pos); pos += e.length; });
  const out = new Uint8Array(pos), ov = new DataView(out.buffer);
  ov.setInt32(0, cnt, true);
  entries.forEach((e, i) => { ov.setInt32(4 + i * 4, o2[i], true); if (e) out.set(e, o2[i]); });
  return out;
}

/** Neue BSP-Datei: alle Lumps unverändert, nur der Entity-Lump (und mit repl der Textur-Lump) wird ersetzt. */
export function withEntities(b: BspFile, text: string, repl?: TexReplacement): Uint8Array {
  const src = new Uint8Array(b.buf), enc = new Uint8Array(text.length + 1);
  for (let i = 0; i < text.length; i++) enc[i] = text.charCodeAt(i) & 255;
  const parts: Uint8Array[] = [], tl = repl && Object.keys(repl).length ? texLump(b, repl) : null;
  for (let i = 0; i < NLUMPS; i++) parts.push(i === LUMP.ENT ? enc : (i === LUMP.TEX && tl) ? tl : src.subarray(b.lumps[i].ofs, b.lumps[i].ofs + b.lumps[i].len));
  let pos = 4 + NLUMPS * 8; const offs: number[] = [];
  parts.forEach((p) => { pos = (pos + 3) & ~3; offs.push(pos); pos += p.length; });
  const out = new Uint8Array(pos), dv = new DataView(out.buffer);
  dv.setInt32(0, 30, true);
  parts.forEach((p, i) => { dv.setInt32(4 + i * 8, offs[i], true); dv.setInt32(8 + i * 8, p.length, true); out.set(p, offs[i]); });
  return out;
}
